import React from "react";

import { ImageUpload } from "../BuilderUtils";
import ChapterField from "../ChapterField";
import ChartInspector from "../ChartInspector";
import ViewComposer from "../ViewComposer";
import { PANEL_MODES, STEP_KINDS, KINDS_WITHOUT_CONTENT, stepWithKind } from "../stepKinds";

/**
 * L'INSPECTEUR D'UNE ÉTAPE DU MODULE TERRITOIRE — quatre sections.
 *
 * CE QU'IL ÉTAIT. Ces quatre panneaux vivaient dans `StepEditor`, une PAGE
 * PLEIN ÉCRAN montée à la place du composeur. Éditer une étape faisait donc
 * disparaître l'atelier, la pile, et — le plus gênant — le bouton
 * « Enregistrer », qui appartient à la page : on composait un module sans
 * qu'aucune sauvegarde ne soit visible nulle part, et il fallait ressortir
 * pour la retrouver.
 *
 * Ils sont maintenant un inspecteur du composeur comme les autres. Le module
 * n'est plus un outil à part : c'est un type de page, avec ses sections, dans
 * la coque qui détient le brouillon.
 *
 * POURQUOI IL NE REND QUE L'INSPECTEUR, ET JAMAIS LA CARTE. La scène 3D coûte
 * un datapack complet à charger (`rivers.json` fait 4,8 Mo) : elle est montée
 * UNE fois par le composeur et ne bouge plus, quel que soit l'onglet ouvert.
 * Un inspecteur qui monterait sa propre carte annulerait ça à chaque clic.
 */
export default function StepInspector({
  section, step, stepIndex, steps, territoryId, availableLayers, layerCatalog, themes,
  catalog, token, placingPoint, onPlacingPointChange, onChangeStep,
}) {
  const setField = (field, value) => onChangeStep({ ...step, [field]: value });
  const setSub = (group, field, value) =>
    onChangeStep({ ...step, [group]: { ...(step[group] || {}), [field]: value } });

  /* ── Cartes de quizz ──
     Une carte a DEUX images, qui ne servent pas à la même chose : `image_url`
     illustre la face cachée (recadrée en fond, elle donne envie),
     `reveal_image_url` porte l'explication de la face révélée (un schéma, une
     coupe) et s'agrandit d'un clic. Les cartes écrites avant cette distinction
     n'ont que la première, ce qui reste un cas valable. */
  const addCard = () =>
    setSub("quiz", "cards", [
      ...(step.quiz?.cards || []),
      { title: "", description: "", image_url: "", reveal_image_url: "" },
    ]);
  const updateCard = (idx, field, value) => {
    const cards = [...(step.quiz?.cards || [])];
    cards[idx] = { ...cards[idx], [field]: value };
    setSub("quiz", "cards", cards);
  };
  const removeCard = (idx) => {
    const cards = [...(step.quiz?.cards || [])];
    cards.splice(idx, 1);
    setSub("quiz", "cards", cards);
  };

  /* ── Quizz mouvant ── */
  const addOption = () =>
    setSub("quiz_mouvant", "options", [...(step.quiz_mouvant?.options || []), { text: "" }]);
  const updateOption = (idx, value) => {
    const options = [...(step.quiz_mouvant?.options || [])];
    options[idx] = { text: value };
    setSub("quiz_mouvant", "options", options);
  };
  const removeOption = (idx) => {
    const options = [...(step.quiz_mouvant?.options || [])];
    options.splice(idx, 1);
    setSub("quiz_mouvant", "options", options);
  };

  /* ── Zones d'intérêt ── */
  const updatePoi = (idx, field, value) => {
    const points = [...(step.points_of_interest?.points || [])];
    points[idx] = { ...points[idx], [field]: value };
    setSub("points_of_interest", "points", points);
  };
  const removePoi = (idx) => {
    const points = [...(step.points_of_interest?.points || [])];
    points.splice(idx, 1);
    setSub("points_of_interest", "points", points);
  };

  if (section === "type") {
    return (
      <section className="step-editor-section">
        <h2 className="step-editor-section-title">Type d'étape</h2>
        <p className="step-editor-section-hint">
          Ce que le participant voit à côté de la carte. Changer de type conserve le cadrage
          et les couches.
        </p>
        <div className="step-picker-grid">
          {STEP_KINDS.map((kind) => (
            <button
              key={kind.id}
              type="button"
              className={`step-picker-card${step.kind === kind.id ? " is-active" : ""}`}
              onClick={() => onChangeStep(stepWithKind(step, kind.id))}
              aria-pressed={step.kind === kind.id}
            >
              {kind.sketch}
              <span className="step-picker-label">{kind.label}</span>
              <span className="step-picker-hint">{kind.hint}</span>
            </button>
          ))}
        </div>

        <ChapterField
          steps={steps}
          stepIndex={stepIndex}
          onChange={(chapter) => setField("chapter", chapter)}
        />
      </section>
    );
  }

  if (section === "frame") {
    return (
      <section className="step-editor-section">
        <h2 className="step-editor-section-title">Position du contenu</h2>
        <p className="step-editor-section-hint">
          Décide de la surface de carte qui reste visible. L'animateur peut toujours
          agrandir ou masquer le panneau en séance.
        </p>
        <div className="step-picker-grid">
          {PANEL_MODES.map((mode) => (
            <button
              key={mode.id}
              type="button"
              className={`step-picker-card${(step.panel_mode || "normal") === mode.id ? " is-active" : ""}`}
              onClick={() => setField("panel_mode", mode.id)}
              aria-pressed={(step.panel_mode || "normal") === mode.id}
            >
              {mode.sketch}
              <span className="step-picker-label">{mode.label}</span>
              <span className="step-picker-hint">{mode.hint}</span>
            </button>
          ))}
        </div>

        <h2 className="step-editor-section-title" style={{ marginTop: 22 }}>Cadrage</h2>
        <p className="step-editor-section-hint">
          Orientez la carte à la souris, puis capturez — les raccourcis sont sur le canevas.
        </p>
        <div className={`step-editor-status${step.camera ? " is-ok" : " is-warn"}`}>
          {step.camera
            ? "Cadrage capturé pour cette étape."
            : "Aucun cadrage : l'étape s'ouvrira sur la vue par défaut."}
        </div>
        {step.camera && (
          <button type="button" className="builder-btn-secondary" onClick={() => setField("camera", null)}>
            Oublier ce cadrage
          </button>
        )}
      </section>
    );
  }

  if (section === "map") {
    return (
      <section className="step-editor-section">
        <h2 className="step-editor-section-title">Ce que la carte montre</h2>
        {(availableLayers || []).length === 0 ? (
          <p className="builder-empty-inline">
            Aucune couche activée pour ce module — voir « Couches disponibles » dans l'onglet Module.
          </p>
        ) : (
          <ViewComposer
            availableLayers={availableLayers}
            layerCatalog={layerCatalog}
            themes={themes}
            activeLayers={step.active_layers || []}
            layerSettings={step.layer_settings || {}}
            catalog={catalog}
            onChange={(patch) => onChangeStep({
              ...step,
              ...(patch.activeLayers ? { active_layers: patch.activeLayers } : {}),
              ...(patch.layerSettings ? { layer_settings: patch.layerSettings } : {}),
            })}
          />
        )}
      </section>
    );
  }

  if (KINDS_WITHOUT_CONTENT.includes(step.kind)) {
    return (
      <section className="step-editor-section">
        <h2 className="step-editor-section-title">Contenu</h2>
        <p className="builder-empty-inline">
          Une étape « Explorer » n'a pas de contenu : la carte reste nue et l'animateur
          la commente. Réglez son cadrage et ses couches.
        </p>
      </section>
    );
  }

  return (
    <section className="step-editor-section">
      <h2 className="step-editor-section-title">Contenu</h2>

      {step.kind === "message" && (
        <>
          <label className="builder-label">
            Texte
            <textarea
              className="builder-textarea"
              rows={10}
              value={step.message?.text || ""}
              onChange={(e) => setSub("message", "text", e.target.value)}
              placeholder="Message affiché à cette étape…"
            />
          </label>
          <ImageUpload
            label="Image (optionnel)"
            value={step.message?.media_url || ""}
            onChange={(url) => setSub("message", "media_url", url)}
            token={token}
          />
        </>
      )}

      {step.kind === "slide" && (
        <>
          <p className="step-editor-section-hint">
            Une image, rien d'autre. Préparez-la au format 16:9 (1920×1080) :
            c'est le cadre dans lequel elle sera projetée, une image d'un autre
            format y sera posée en entier, sur fond neutre.
          </p>
          <ImageUpload
            label="Image de la diapo"
            value={step.slide?.image_url || ""}
            onChange={(url) => setSub("slide", "image_url", url)}
            token={token}
          />
          <label className="builder-label">
            Description pour les lecteurs d'écran (optionnel)
            <input
              className="builder-input"
              value={step.slide?.alt || ""}
              onChange={(e) => setSub("slide", "alt", e.target.value)}
              placeholder="Ce que montre l'image, en une phrase…"
            />
          </label>
        </>
      )}

      {step.kind === "graph" && (
        <ChartInspector
          territoryId={territoryId}
          charts={step.charts || []}
          onChange={(charts) => setField("charts", charts)}
        />
      )}

      {step.kind === "quiz" && (
        <>
          <label className="builder-label">
            Question (optionnel)
            <input
              className="builder-input"
              value={step.quiz?.question || ""}
              onChange={(e) => setSub("quiz", "question", e.target.value)}
              placeholder="Ex : quel est l'effet du changement climatique sur les sols ?"
            />
          </label>
          <div className="builder-section-header" style={{ marginTop: 12 }}>
            <h3 className="builder-subsection-title">Cartes ({(step.quiz?.cards || []).length})</h3>
            <button className="builder-btn-secondary" onClick={addCard}>+ Ajouter</button>
          </div>
          <p className="step-editor-section-hint">
            Chaque carte a deux faces. Face cachée : un titre, et une image
            d'illustration facultative, recadrée en fond — elle donne envie, elle
            ne dit rien. Face révélée : l'explication, avec sa propre image, que
            l'animateur peut agrandir en plein écran pendant la séance.
          </p>
          {(step.quiz?.cards || []).length === 0 && (
            <div className="builder-empty">Aucune carte. Cliquez sur « + Ajouter ».</div>
          )}
          {(step.quiz?.cards || []).map((card, idx) => (
            <div key={idx} className="step-editor-card-row">
              <span className="step-editor-card-side">Face cachée</span>
              <input
                className="builder-input"
                value={card.title}
                onChange={(e) => updateCard(idx, "title", e.target.value)}
                placeholder="Titre de la carte"
              />
              <ImageUpload
                label="Image d'illustration (optionnel, recadrée en fond)"
                value={card.image_url || ""}
                onChange={(url) => updateCard(idx, "image_url", url)}
                token={token}
              />

              <span className="step-editor-card-side">Face révélée</span>
              <textarea
                className="builder-textarea"
                rows={3}
                value={card.description}
                onChange={(e) => updateCard(idx, "description", e.target.value)}
                placeholder="Explication révélée au clic"
              />
              <ImageUpload
                label="Image d'explication (optionnel, agrandissable)"
                value={card.reveal_image_url || ""}
                onChange={(url) => updateCard(idx, "reveal_image_url", url)}
                token={token}
              />

              <button className="builder-icon-btn danger" onClick={() => removeCard(idx)}>
                ✕ Supprimer la carte
              </button>
            </div>
          ))}
        </>
      )}

      {step.kind === "quiz_mouvant" && (
        <>
          <label className="builder-label">
            Question
            <input
              className="builder-input"
              value={step.quiz_mouvant?.question || ""}
              onChange={(e) => setSub("quiz_mouvant", "question", e.target.value)}
              placeholder="Ex : quel usage consomme le plus d'eau en été ?"
            />
          </label>
          <ImageUpload
            label="Image d'illustration (optionnel)"
            value={step.quiz_mouvant?.image_url || ""}
            onChange={(url) => setSub("quiz_mouvant", "image_url", url)}
            token={token}
          />
          <div className="builder-section-header" style={{ marginTop: 12 }}>
            <h3 className="builder-subsection-title">
              Propositions ({(step.quiz_mouvant?.options || []).length})
            </h3>
            <button className="builder-btn-secondary" onClick={addOption}>+ Ajouter</button>
          </div>
          <p className="step-editor-section-hint">
            Chaque proposition s'affiche avec un grand numéro : les participants se
            positionnent physiquement dans la salle.
          </p>
          {(step.quiz_mouvant?.options || []).map((option, idx) => (
            <div key={idx} className="builder-consequence-row">
              <span style={{ fontWeight: 700, minWidth: 20 }}>{idx + 1}.</span>
              <input
                className="builder-input"
                value={option.text}
                onChange={(e) => updateOption(idx, e.target.value)}
                placeholder={`Proposition ${idx + 1}`}
              />
              <button className="builder-icon-btn danger" onClick={() => removeOption(idx)}>✕</button>
            </div>
          ))}
        </>
      )}

      {step.kind === "points_of_interest" && (
        <>
          <div className="builder-section-header">
            <h3 className="builder-subsection-title">
              Points ({(step.points_of_interest?.points || []).length})
            </h3>
            <button
              type="button"
              className="builder-btn-secondary"
              style={placingPoint ? { outline: "2px solid #0ea5e9" } : undefined}
              onClick={() => onPlacingPointChange(!placingPoint)}
              disabled={!territoryId}
            >
              {placingPoint ? "📍 Cliquez sur la carte…" : "📍 Ajouter un point"}
            </button>
          </div>
          {(step.points_of_interest?.points || []).length === 0 && (
            <div className="builder-empty">
              Aucun point. Cliquez sur « Ajouter un point » puis sur la carte.
            </div>
          )}
          {(step.points_of_interest?.points || []).map((point, idx) => (
            <div key={point.point_id || idx} className="step-editor-card-row">
              <input
                className="builder-input"
                value={point.title}
                onChange={(e) => updatePoi(idx, "title", e.target.value)}
                placeholder="Titre du point"
              />
              <textarea
                className="builder-textarea"
                rows={3}
                value={point.text}
                onChange={(e) => updatePoi(idx, "text", e.target.value)}
                placeholder="Texte affiché dans le panneau"
              />
              <ImageUpload
                label="Image (optionnel)"
                value={point.image_url || ""}
                onChange={(url) => updatePoi(idx, "image_url", url)}
                token={token}
              />
              <button className="builder-icon-btn danger" onClick={() => removePoi(idx)}>
                ✕ Supprimer le point
              </button>
            </div>
          ))}
        </>
      )}
    </section>
  );
}
